<script setup lang="ts">
import { computed, ref } from 'vue';

interface SwiperImage {
  url: string;
  alt?: string;
}

const props = defineProps<{
  images: SwiperImage[];
  title: string;
}>();

const { t } = useI18n();

const rootEl = ref<HTMLElement | null>(null);
const currentIndex = ref(0);
const dragging = ref(false);
const dragX = ref(0);
const startX = ref(0);
const startY = ref(0);
const pointerId = ref<number | null>(null);
const justDragged = ref(false);
const containerWidth = ref(0);

const DRAG_THRESHOLD_PX = 8;
const SNAP_THRESHOLD_RATIO = 0.18;
const DOT_WINDOW = 5;

const total = computed(() => props.images.length);
const wrap = (i: number) => ((i % total.value) + total.value) % total.value;

const prevImage = computed(() => props.images[wrap(currentIndex.value - 1)]);
const currentImage = computed(() => props.images[currentIndex.value]);
const nextImage = computed(() => props.images[wrap(currentIndex.value + 1)]);

const stripStyle = computed(() => ({
  transform: `translate3d(calc(-100% + ${dragX.value}px), 0, 0)`,
}));

const dotsWindow = computed<number[]>(() => {
  const n = total.value;
  if (n <= DOT_WINDOW) return Array.from({ length: n }, (_, i) => i);
  const half = Math.floor(DOT_WINDOW / 2);
  let start = Math.max(0, currentIndex.value - half);
  if (start + DOT_WINDOW > n) start = n - DOT_WINDOW;
  return Array.from({ length: DOT_WINDOW }, (_, i) => start + i);
});

function onPointerDown(e: PointerEvent) {
  if (e.pointerType === 'mouse' && e.button !== 0) return;
  pointerId.value = e.pointerId;
  startX.value = e.clientX;
  startY.value = e.clientY;
  containerWidth.value = rootEl.value?.clientWidth ?? 0;
  dragX.value = 0;
}

function onPointerMove(e: PointerEvent) {
  if (pointerId.value !== e.pointerId) return;
  const dx = e.clientX - startX.value;
  const dy = e.clientY - startY.value;
  if (!dragging.value) {
    if (Math.abs(dy) > Math.abs(dx)) return;
    if (Math.abs(dx) <= DRAG_THRESHOLD_PX) return;
    dragging.value = true;
    try {
      rootEl.value?.setPointerCapture(e.pointerId);
    } catch {
      // setPointerCapture can throw if the pointer is no longer active
    }
  }
  const max = containerWidth.value || 1;
  // Clamp slightly inside ±max so snap target ±max is always a state change
  // (otherwise transitionend never fires when finger overshoots a slot).
  dragX.value = Math.max(-(max - 1), Math.min(max - 1, dx));
}

function onPointerUp(e: PointerEvent) {
  if (pointerId.value !== e.pointerId) return;
  pointerId.value = null;
  if (!dragging.value) return;
  const width = containerWidth.value || 1;
  const ratio = dragX.value / width;
  // Releasing dragging re-enables the CSS transition so the snap animates.
  dragging.value = false;
  if (ratio <= -SNAP_THRESHOLD_RATIO) {
    dragX.value = -width;
  } else if (ratio >= SNAP_THRESHOLD_RATIO) {
    dragX.value = width;
  } else {
    dragX.value = 0;
  }
  justDragged.value = true;
  setTimeout(() => {
    justDragged.value = false;
  }, 100);
}

function onPointerCancel(e: PointerEvent) {
  if (pointerId.value !== e.pointerId) return;
  pointerId.value = null;
  if (dragging.value) {
    dragX.value = 0;
    dragging.value = false;
  }
}

function onTransitionEnd() {
  if (dragX.value === 0) return;
  const dir = dragX.value < 0 ? 1 : -1;
  const next = wrap(currentIndex.value + dir);
  dragging.value = true;
  currentIndex.value = next;
  dragX.value = 0;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      dragging.value = false;
    });
  });
}

function onClickCapture(e: MouseEvent) {
  if (justDragged.value) {
    e.preventDefault();
    e.stopPropagation();
  }
}
</script>

<template>
  <div
    ref="rootEl"
    class="relative w-full h-full overflow-hidden touch-pan-y select-none"
    role="region"
    :aria-roledescription="t('apartmentList.swiper.aria')"
    :aria-label="title"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerCancel"
    @click.capture="onClickCapture"
  >
    <div
      class="absolute inset-0 flex will-change-transform"
      :class="dragging ? '' : 'motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-out'"
      :style="stripStyle"
      @transitionend="onTransitionEnd"
    >
      <div class="w-full h-full flex-shrink-0">
        <NuxtImg
          :src="prevImage.url"
          :alt="prevImage.alt ?? title"
          width="1440"
          height="1080"
          format="webp"
          loading="lazy"
          sizes="xs:100vw sm:50vw"
          draggable="false"
          class="w-full h-full object-cover pointer-events-none"
        />
      </div>
      <div class="w-full h-full flex-shrink-0">
        <NuxtImg
          :src="currentImage.url"
          :alt="currentImage.alt ?? title"
          width="1440"
          height="1080"
          format="webp"
          loading="lazy"
          sizes="xs:100vw sm:50vw"
          draggable="false"
          class="w-full h-full object-cover pointer-events-none"
        />
      </div>
      <div class="w-full h-full flex-shrink-0">
        <NuxtImg
          :src="nextImage.url"
          :alt="nextImage.alt ?? title"
          width="1440"
          height="1080"
          format="webp"
          loading="lazy"
          sizes="xs:100vw sm:50vw"
          draggable="false"
          class="w-full h-full object-cover pointer-events-none"
        />
      </div>
    </div>

    <ol
      v-if="total > 1"
      class="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 pointer-events-none"
      aria-hidden="true"
    >
      <li
        v-for="i in dotsWindow"
        :key="i"
        class="rounded-full transition-all duration-200"
        :class="i === currentIndex
          ? 'w-1.5 h-1.5 bg-white shadow-sm'
          : 'w-1.5 h-1.5 bg-white/55'"
      />
    </ol>
  </div>
</template>
